import React, { lazy } from "react"
import { Common } from "lib/types"
import { ReactComponent as SettingsIcon } from "images/icons/gear.svg"
import { ReactComponent as WorkflowIcon } from "images/icons/circle-nodes.svg"
import { PermissionGroups } from "../constants"

const Settings = lazy(() => import("views/Settings/Settings"))
const UsagePlan = lazy(() => import("views/Settings/UsagePlan"))
const ExternalEvents = lazy(() => import("views/Settings/ExternalEvents/ExternalEvents"))
const CustomReasonCodes = lazy(() => import("views/Settings/CustomReason/CustomReasonCodes"))
const CustomStates = lazy(() => import("views/Settings/CustomStates/CustomStates"))
const Workflows = lazy(() => import("views/Workflows/Workflows"))
const ShowWorkflows = lazy(() => import("views/Workflows/ShowWorkflows"))
const CreateWorkflow = lazy(() => import("views/Workflows/CreateWorkflow"))
const EditWorkflow = lazy(() => import("views/Workflows/EditWorkflow"))
const ShippingFallbacks = lazy(() => import("views/Settings/ShippingFallbacks"))
const Cost = lazy(() => import("views/Settings/Cost"))
const ApiClients = lazy(() => import("views/Settings/ApiClients/ApiClients"))
const Credentials = lazy(() => import("views/Settings/Credentials/Credentials"))
const CreateApiDestination = lazy(() => import("views/Settings/ExternalEvents/DestinationAPIs/CreateApiDestination"))
const UpdateApiDestination = lazy(() => import("views/Settings/ExternalEvents/DestinationAPIs/UpdateApiDestination"))
const ShowUsers = lazy(() => import("views/Users/ShowUsers"))
const User = lazy(() => import("views/Users/User"))
const Providers = lazy(() => import("views/Settings/Providers/Providers"))
const ShowProviders = lazy(() => import("views/Settings/Providers/ShowProviders"))
const AdyenConfiguration = lazy(() => import("views/Settings/Providers/Adyen/AdyenConfiguration"))
const KustomConfig = lazy(() => import("views/Settings/Providers/Kustom/KustomConfig"))
const KustomInPersonPaymentsConfiguration = lazy(() => import("views/Settings/Providers/KustomInPersonPayments/KustomInPersonPaymentsConfiguration"))
const SveaConfiguration = lazy(() => import("views/Settings/Providers/Svea/SveaConfiguration"))
const IngridConfiguration = lazy(() => import("views/Settings/Providers/Ingrid/IngridConfiguration"))
const Retain24Config = lazy(() => import("views/Settings/Providers/Retain24/Retain24Config"))
const ShoplabGiftCardConfig = lazy(() => import("views/Settings/Providers/Shoplab/ShoplabGiftCardConfig"))
const NShiftConfiguration = lazy(() => import("views/Settings/Providers/NShift/NShiftConfiguration"))
const VoyadoConfiguration = lazy(() => import("views/Settings/Providers/Voyado/VoyadoConfiguration"))
const WalleyConfiguration = lazy(() => import("views/Settings/Providers/Walley/WalleyConfiguration"))
const BonusConfiguration = lazy(() => import("views/Settings/Providers/Bonus/BonusConfiguration"))
const ExternalPriceConfiguration = lazy(() => import("views/Settings/Providers/ExternalPrice/ExternalPriceConfiguration"))
const QliroOneConfiguration = lazy(() => import("views/Settings/Providers/Qliro/QliroOneConfiguration"))
const QliroLoyaltyConfiguration = lazy(() => import("views/Settings/Providers/Qliro/QliroLoyaltyConfiguration"))
const AvardaConfiguration = lazy(() => import("views/Settings/Providers/Avarda/AvardaConfiguration"))
const ZaverConfiguration = lazy(() => import("views/Settings/Providers/Zaver/ZaverConfiguration"))
const LedyerConfiguration = lazy(() => import("views/Settings/Providers/Ledyer/LedyerConfiguration"))
const CustomPaymentConfiguration = lazy(() => import("views/Settings/Providers/CustomPayment/CustomPaymentConfiguration"))
const CustomShippingConfiguration = lazy(() => import("views/Settings/Providers/CustomShipping/CustomShippingConfiguration"))
const TaxJarConfiguration = lazy(() => import("views/Settings/Providers/TaxJar/TaxJarConfiguration"))
const AvalaraConfiguration = lazy(() => import("views/Settings/Providers/Avalara/AvalaraConfiguration"))
const CustomGiftCardConfiguration = lazy(() => import("views/Settings/Providers/CustomGiftCard/CustomGiftCardConfiguration"))

export const settingsRoutes: Common.Views = [
  {
    element: <Settings />,
    path: "/settings",
    title: "Settings",
    icon: SettingsIcon,
    sortOrder: 7,
    children: [
      {
        element: <Providers />,
        path: "/settings/providers",
        title: "Providers",
        parentPath: "/settings",
        requiredGroups: PermissionGroups.providers.read,
        section: "Connections",
        children: [
          {
            element: <ShowProviders />,
            path: "/settings/providers",
            title: "Show Providers",
            parentPath: "/settings"
          },
          {
            element: <AdyenConfiguration />,
            path: "/settings/providers/adyen",
            title: "Adyen",
            parentPath: "/settings/providers"
          },
          {
            element: <KustomConfig />,
            path: "/settings/providers/kustom",
            title: "Kustom",
            parentPath: "settings/providers"
          },
          {
            element: <KustomInPersonPaymentsConfiguration />,
            path: "/settings/providers/kustom-in-person-payments",
            title: "Kustom in-person payments",
            parentPath: "/settings/providers"
          },
          {
            element: <SveaConfiguration />,
            path: "/settings/providers/svea",
            title: "Svea",
            parentPath: "/settings/providers"
          },
          {
            element: <IngridConfiguration />,
            path: "/settings/providers/ingrid",
            title: "Ingrid",
            parentPath: "/settings/providers"
          },
          {
            element: <Retain24Config />,
            path: "/settings/providers/retain24",
            title: "Retain24",
            parentPath: "/settings/providers"
          },
          {
            element: <ShoplabGiftCardConfig />,
            path: "/settings/providers/shoplab",
            title: "Shoplab",
            parentPath: "/settings/providers"
          },
          {
            element: <NShiftConfiguration />,
            path: "/settings/providers/Nshift",
            title: "nShift",
            parentPath: "/settings/providers"
          },
          {
            element: <VoyadoConfiguration />,
            path: "/settings/providers/voyado",
            title: "Voyado",
            parentPath: "/settings/providers"
          },
          {
            element: <WalleyConfiguration />,
            path: "/settings/providers/walley",
            title: "Walley",
            parentPath: "/settings/providers"
          },
          {
            element: <BonusConfiguration />,
            path: "/settings/providers/bonus",
            title: "Bonus providers",
            parentPath: "/settings/providers"
          },
          {
            element: <ExternalPriceConfiguration />,
            path: "/settings/providers/external-price",
            title: "External Price providers",
            parentPath: "/settings/providers"
          },
          {
            element: <QliroOneConfiguration />,
            path: "/settings/providers/qliro",
            title: "Qliro One",
            parentPath: "/settings/providers"
          },
          {
            element: <QliroLoyaltyConfiguration />,
            path: "/settings/providers/qliro-loyalty",
            title: "Qliro Loyalty",
            parentPath: "/settings/providers"
          },
          {
            element: <AvardaConfiguration />,
            path: "/settings/providers/avarda",
            title: "Avarda",
            parentPath: "/settings/providers"
          },
          {
            element: <ZaverConfiguration />,
            path: "/settings/providers/zaver",
            title: "Zaver",
            parentPath: "/settings/providers"
          },
          {
            element: <LedyerConfiguration />,
            path: "/settings/providers/ledyer",
            title: "Ledyer",
            parentPath: "/settings/providers"
          },
          {
            element: <CustomPaymentConfiguration />,
            path: "/settings/providers/custom-payment",
            title: "Custom Payment",
            parentPath: "/settings/providers"
          },
          {
            element: <CustomShippingConfiguration />,
            path: "/settings/providers/custom-shipping",
            title: "Custom Shipping",
            parentPath: "/settings/providers"
          },
          {
            element: <TaxJarConfiguration />,
            path: "/settings/providers/tax-jar",
            title: "TaxJar",
            parentPath: "/settings/providers"
          },
          {
            element: <AvalaraConfiguration />,
            path: "/settings/providers/avalara",
            title: "Avalara",
            parentPath: "/settings/providers"
          },
          {
            element: <CustomGiftCardConfiguration />,
            path: "/settings/providers/bsg-gift-card",
            title: "Brink Custom Gift Card",
            parentPath: "/settings/providers"
          }
        ]
      }
    ]
  },
  {
    element: <ExternalEvents />,
    path: "/settings/external-events",
    parentPath: "/settings",
    title: "External events",
    requiredGroups: PermissionGroups.externalEvents.read
  },
  {
    element: <CreateApiDestination />,
    path: "/settings/external-events/create-api-destination",
    parentPath: "/settings/external-events",
    title: "Create API destination",
    requiredGroups: PermissionGroups.externalEvents.write
  },
  {
    element: <UpdateApiDestination />,
    path: "/settings/external-events/update-api-destination/:name",
    parentPath: "/settings/external-events",
    title: "Update API destination",
    requiredGroups: PermissionGroups.externalEvents.read
  },
  {
    element: <CustomReasonCodes />,
    path: "/settings/order-definitions",
    parentPath: "/settings",
    title: "Order definitions",
    section: "Order settings"
  },
  {
    element: <CustomStates />,
    path: "/settings/custom-states",
    parentPath: "/settings",
    title: "Custom states"
  },
  {
    element: <Workflows />,
    path: "/settings/workflows",
    title: "Workflows",
    icon: WorkflowIcon,
    sortOrder: 5,
    requiredGroups: PermissionGroups.workflows.read,
    configKey: "workflowsEnabled",
    children: [
      {
        element: <ShowWorkflows />,
        path: "/settings/workflows/",
        title: "Show workflows",
        parentPath: "/settings/workflows",
        section: "Workflows"
      },
      {
        element: <CreateWorkflow />,
        path: "/settings/workflows/create",
        title: "Create workflow",
        parentPath: "/settings/workflows",
        requiredGroups: PermissionGroups.workflows.write
      },
      {
        element: <EditWorkflow />,
        path: "/settings/workflows/:id",
        title: "Edit workflow",
        parentPath: "/settings/workflows"
      }
    ]
  },
  {
    element: <ShippingFallbacks />,
    path: "/settings/shipping-fallbacks",
    parentPath: "/settings",
    title: "Shipping fallbacks",
    requiredGroups: PermissionGroups.checkouts.read
  },
  {
    element: <Cost />,
    path: "/settings/cost",
    parentPath: "/settings",
    title: "Cost",
    requiredGroups: PermissionGroups.costs.read
  },
  {
    element: <UsagePlan />,
    path: "/settings/usage-plan",
    parentPath: "/settings",
    title: "Usage plan",
    section: "Brink configurations",
    requiredGroups: PermissionGroups.system.read
  },
  {
    element: <Credentials />,
    path: "/settings/credentials",
    parentPath: "/settings",
    title: "Credentials",
    requiredGroups: PermissionGroups.system.read
  },
  {
    element: <ApiClients />,
    path: "/settings/api-configs",
    parentPath: "/settings",
    title: "API clients",
    requiredGroups: PermissionGroups.apiClients.read
  },
  {
    element: <ShowUsers />,
    path: "/settings/manage-users",
    parentPath: "/settings",
    title: "Manage users",
    section: "Users",
    requiredGroups: PermissionGroups.users.read
  },
  {
    element: <User />,
    path: "/user"
  }
]
